<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash-es'
import dayjs from '@/lib/dayjs'
import { useNotification } from '@kyvg/vue3-notification'
import { apiPost } from '@/lib/http'
import Toolbar from './Calendar/Toolbar.vue'
import MonthView from './Calendar/MonthView.vue'
import WeekView from './Calendar/WeekView.vue'
import DayView from './Calendar/DayView.vue'
import ListView from './Calendar/ListView.vue'
import type { WebinarEvent, WebinarCalendarEvent, WebinarPageContext, ViewMode } from './types'

const props = defineProps<{
  urls?: Record<string, string>
  pageContext?: WebinarPageContext
  viewMode: ViewMode
}>()

const emit = defineEmits<{
  (e: 'update:view-mode', value: ViewMode): void
  (e: 'open-event', id: number): void
  (e: 'purchase-event', id: number): void
  (e: 'purchase-video', id: number): void
}>()

const { notify } = useNotification()

const loading = ref(true)
const viewDate = ref(new Date())
const currentDate = ref(new Date())
const query = ref('')
const filters = ref<Record<string, unknown>>({})
const events = ref<WebinarCalendarEvent[]>([])
const listPage = ref(1)
const listTotal = ref(0)
const listPageSize = ref(20)

let dateUpdateIntval: ReturnType<typeof setInterval> | null = null

const filterParams = computed(() => ({ ...filters.value, search_text: query.value || undefined }))
const listPageSizeOptions = [
  { value: 20, text: 'Показывать по 20' },
  { value: 50, text: 'Показывать по 50' },
  { value: 100, text: 'Показывать по 100' }
]

function updateCurrentDate() {
  currentDate.value = dayjs(new Date()).startOf('minute').toDate()
}

const fetchDelayed = debounce(() => fetchWebinars(), 500)

onMounted(() => {
  updateCurrentDate()
  const beforeNextMin = 60000 - (Date.now() % 60000)
  setTimeout(() => {
    updateCurrentDate()
    dateUpdateIntval = setInterval(updateCurrentDate, 60000)
  }, beforeNextMin)
  fetchWebinars()
})

onBeforeUnmount(() => {
  if (dateUpdateIntval) clearInterval(dateUpdateIntval)
})

watch(viewDate, () => {
  events.value = []
  fetchDelayed()
})

watch(
  () => props.viewMode,
  val => {
    emit('update:view-mode', val)
    events.value = []
    if (val === 'list') listPage.value = 1
    fetchDelayed()
  }
)

watch(filterParams, () => {
  if (props.viewMode === 'list') listPage.value = 1
  fetchDelayed()
})

watch(listPage, () => {
  if (props.viewMode === 'list') fetchDelayed()
})

watch(listPageSize, () => {
  if (props.viewMode === 'list') {
    listPage.value = 1
    fetchDelayed()
  }
})

async function fetchWebinars() {
  loading.value = true

  let timeFrom
  let timeTo
  if (props.viewMode === 'month') {
    timeFrom = dayjs(viewDate.value).startOf('month').startOf('week')
    timeTo = dayjs(viewDate.value).endOf('month').endOf('week')
  } else if (props.viewMode !== 'list') {
    timeFrom = dayjs(viewDate.value).startOf(props.viewMode)
    timeTo = dayjs(viewDate.value).endOf(props.viewMode)
  }

  const payload: Record<string, unknown> = {
    date_from: timeFrom?.format('YYYY-MM-DD'),
    date_to: timeTo?.format('YYYY-MM-DD'),
    ...filterParams.value
  }

  if (props.viewMode === 'list') {
    payload.slice_from = (listPage.value - 1) * listPageSize.value
    payload.slice_to = listPage.value * listPageSize.value
  }

  const resp = await apiPost<WebinarEvent[]>(props.urls?.index || '', payload)
  if (resp.failed) return
  events.value = resp.data.map(event => ({
    ...event,
    start_time: dayjs(event.start_time! * 1000).tz('Europe/Moscow'),
    end_time: dayjs(event.end_time! * 1000).tz('Europe/Moscow'),
    access_seminars: event.access_seminars || [],
    access_acecourses: event.access_acecourses || [],
    access_categories: event.access_categories || ''
  })) as WebinarCalendarEvent[]

  if (props.viewMode === 'list') listTotal.value = resp.count ?? resp.data.length
  loading.value = false
}

async function likeEvent(id: number) {
  const eventIndex = events.value.findIndex(event => event.id === id)
  if (eventIndex === -1) return
  const row = events.value[eventIndex]
  const newValue = !row.me_liked

  try {
    const resp = await apiPost(props.urls?.like || '', {
      webinar_id: id,
      like: newValue
    })
    if (resp.failed) throw resp.message
  } catch (error) {
    let title = 'Произошла ошибка при добавлении вебинара в избранное.'
    if (typeof error === 'string') title = error
    notify({ type: 'error', title })
    return
  }

  row.me_liked = newValue
}
</script>

<template>
  <div
    class="webinar-calendar"
    :class="{ 'webinar-calendar_loading': loading, 'webinar-calendar_scroll': viewMode === 'list' }"
  >
    <Toolbar
      v-model:filters="filters"
      v-model:view-date="viewDate"
      :view-mode="viewMode"
      :query="query"
      @update:query="
        v => {
          query = v
        }
      "
      @update:view-mode="v => emit('update:view-mode', v)"
    />

    <div v-if="loading" class="webinar-calendar__view webinar-calendar__view_preloader">
      <div class="webinar-calendar__preloader-card">
        <v-icon size="32" class="webinar-calendar__spinner">await</v-icon>
      </div>
    </div>

    <MonthView
      v-if="viewMode === 'month'"
      :view-date="viewDate"
      :current-date="currentDate"
      :events="events"
      @event-click="event => emit('open-event', event.id)"
    />
    <WeekView
      v-if="viewMode === 'week'"
      :view-date="viewDate"
      :current-date="currentDate"
      :events="events"
      @event-click="event => emit('open-event', event.id)"
    />
    <DayView
      v-if="viewMode === 'day'"
      :view-date="viewDate"
      :current-date="currentDate"
      :events="events"
      @event-click="event => emit('open-event', event.id)"
    />
    <ListView
      v-if="viewMode === 'list'"
      :current-date="currentDate"
      :events="events"
      :page-context="pageContext"
      @event-open="id => emit('open-event', id)"
      @event-like="likeEvent"
      @event-purchase="id => emit('purchase-event', id)"
      @video-purchase="id => emit('purchase-video', id)"
    />
    <div v-if="viewMode === 'list'" class="webinar-calendar__pagination">
      <v-pagination
        v-model="listPage"
        :total="listTotal"
        :items-on-page="listPageSize"
        :show-prev="true"
        :show-next="true"
      />
      <v-select
        v-if="listTotal > 20"
        v-model="listPageSize"
        class="webinar-calendar__page-size"
        :items="listPageSizeOptions"
        item-text="text"
        item-value="value"
      />
    </div>
  </div>
</template>

<style lang="scss">
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(180deg);
  }
}

.webinar-calendar {
  display: flex;
  flex-direction: column;

  &_loading {
    position: relative;
  }

  &_scroll {
    min-height: 0;
  }

  &__view {
    flex: 1;

    &_preloader {
      z-index: 2;
      position: absolute;
      width: 100%;
      height: 100%;
      pointer-events: none;

      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  &__spinner {
    animation: spin 2s infinite;
  }

  &__preloader-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    padding: 12px;

    background: white;
    border-radius: 8px;
    box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.1);
  }

  &__pagination {
    margin-top: 16px;
    padding-bottom: 120px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  &__page-size {
    min-width: 180px;
  }
}
</style>
